import * as React from "react";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { useLazyGetCclUserAccountDetailQuery } from "../../../services/cclTokenedGrandCentralApi";
import { validateEmail } from "../../../utilities/generalUtilities";

export type CclAddGroupMemberDrawerProps = {
    open: boolean;
    aagKey: number;
    groupName: string;
    onSave: (email: string, firstName: string, lastName: string, imKey?: number) => void;
    onCancel: () => void;
    isLoading: boolean;
};

const CclAddGroupMemberDrawer: React.FC<CclAddGroupMemberDrawerProps> = (props) => {
    const [email, setEmail] = React.useState<string>("");
    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName, setLastName] = React.useState<string>("");
    const [imKey, setImKey] = React.useState<number | null>(null);
    const [lockNames, setLockNames] = React.useState<boolean>(false);

    const [getUserData] = useLazyGetCclUserAccountDetailQuery();

    React.useEffect(() => {
        if (!props.open) {
            setEmail("");
            setFirstName("");
            setLastName("");
            setImKey(null);
            setLockNames(false);
        }
    }, [props.open]);

    const onEmailChange = (newEmail: string) => {
        setEmail(newEmail);
        if (!validateEmail(newEmail)) {
            setFirstName("");
            setLastName("");
            setImKey(null);
            setLockNames(false);
            return;
        }
        getUserData(newEmail)
            .unwrap()
            .then((res) => {
                if (res.length === 0) {
                    if (lockNames) {
                        setFirstName("");
                        setLastName("");
                        setImKey(null);
                    }
                    setLockNames(false);
                    return;
                }
                setFirstName(res[0].firstName);
                setLastName(res[0].lastName);
                setImKey(res[0].imKey);
                setLockNames(true);
            });
    };

    const handleSave = () => {
        props.onSave(email.trim(), firstName.trim(), lastName.trim(), imKey ?? undefined);
    };

    const canSave =
        email.trim() !== "" &&
        (lockNames || firstName.trim() !== "") &&
        lastName.trim() !== "" &&
        !props.isLoading;

    return (
        <CclDrawer title="Add Group Member" open={props.open} onClose={props.onCancel}>
            <Stack spacing={3} sx={{ p: 2, pt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Adding member to: <strong>{props.groupName}</strong>
                </Typography>
                <Alert severity="info">
                    Caution: If the user is an existing Assessment Administrator then all Sessions,
                    Registrations, Orders, and Inventory assigned to the user will be transferred to
                    the selected Administrator Group and will become visible to any other users in
                    that Administrator Group.
                </Alert>
                <TextField
                    size="small"
                    fullWidth
                    label="Administrator Email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                />
                <TextField
                    size="small"
                    fullWidth
                    label="First Name"
                    value={firstName}
                    disabled={lockNames}
                    onChange={(e) => setFirstName(e.target.value)}
                />
                <TextField
                    size="small"
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    disabled={lockNames}
                    onChange={(e) => setLastName(e.target.value)}
                />
            </Stack>
            <CclDrawerActionBar>
                <CclLoadingButton
                    onClick={handleSave}
                    mode="primary"
                    loading={props.isLoading}
                    disabled={!canSave}
                >
                    Save
                </CclLoadingButton>
                <CclButton onClick={props.onCancel} mode="secondary">
                    Cancel
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default CclAddGroupMemberDrawer;
